import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Grid,
  Container,
  Paper,
  Card,
  CardContent,
  CardHeader,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar
} from '@mui/material';
import { 
  TrendingUp, 
  TrendingDown, 
  People, 
  Business, 
  SwapHoriz,
  AttachMoney
} from '@mui/icons-material';

// Importar componentes de analítica
import DashboardFilters from '../components/DashboardFilters';
import DashboardSummary from '../components/DashboardSummary';
import RevenueChart from '../components/RevenueChart';
import ClientsByDayChart from '../components/ClientsByDayChart';
import ClientsByHourChart from '../components/ClientsByHourChart';
import ClientsByAdvisorChart from '../components/ClientsByAdvisorChart';

const Dashboard = () => {
  // Estado para manejar los filtros
  const [filters, setFilters] = useState({
    branchId: '',
    startDate: '',
    endDate: '',
    periodType: 'monthly'
  });

  // Actualizar filtros
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  // Datos de ejemplo
  const summaryData = [
    { title: 'Transferencias Hoy', value: '15', icon: <SwapHoriz color="primary" />, trend: '+20%' },
    { title: 'Monto Total', value: 'S/. 12,500', icon: <AttachMoney color="primary" />, trend: '+15%' },
    { title: 'Clientes Nuevos', value: '8', icon: <People color="primary" />, trend: '+5%' },
    { title: 'Sucursales Activas', value: '4', icon: <Business color="primary" />, trend: '0%' },
  ];

  const recentTransfers = [
    { id: 1, client: 'Juan Pérez', amount: 'S/. 1,200', date: '2023-05-15 10:30', status: 'Completado' },
    { id: 2, client: 'María López', amount: 'S/. 850', date: '2023-05-15 11:45', status: 'Completado' },
    { id: 3, client: 'Carlos Ruiz', amount: 'S/. 2,000', date: '2023-05-15 12:15', status: 'Pendiente' },
    { id: 4, client: 'Ana Torres', amount: 'S/. 1,500', date: '2023-05-15 13:20', status: 'Completado' },
    { id: 5, client: 'Pedro Gómez', amount: 'S/. 950', date: '2023-05-15 14:05', status: 'Pendiente' },
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 3 }}>
        <Typography variant="h4" gutterBottom>
          Dashboard de Analítica
        </Typography>
        
        {/* Filtros */}
        <DashboardFilters onFilterChange={handleFilterChange} />
        
        {/* Resumen */}
        <DashboardSummary filters={filters} />
        
        {/* Gráficos */}
        <Grid container spacing={3} sx={{ mt: 1 }}>
          {/* Ingresos por periodo */}
          <Grid item xs={12} md={6}>
            <RevenueChart filters={filters} />
          </Grid>
          
          {/* Clientes por asesor */}
          <Grid item xs={12} md={6}>
            <ClientsByAdvisorChart filters={filters} />
          </Grid>
          
          {/* Clientes por día */}
          <Grid item xs={12} md={6}>
            <ClientsByDayChart filters={filters} />
          </Grid>
          
          {/* Clientes por hora */}
          <Grid item xs={12} md={6}>
            <ClientsByHourChart filters={filters} />
          </Grid>
        </Grid>
        
        {/* Tarjetas de resumen */}
        <Grid container spacing={3} sx={{ mt: 4 }}>
          {summaryData.map((item, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper elevation={2} sx={{ borderRadius: 2 }}>
                <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
                  <Avatar sx={{ bgcolor: 'primary.light', mr: 2 }}>
                    {item.icon}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      {item.title}
                    </Typography>
                    <Typography variant="h5">
                      {item.value}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {item.trend.startsWith('+') ? (
                        <TrendingUp fontSize="small" color="success" />
                      ) : item.trend === '0%' ? null : (
                        <TrendingDown fontSize="small" color="error" />
                      )}
                      <Typography 
                        variant="caption" 
                        color={item.trend.startsWith('+') ? 'success.main' : item.trend === '0%' ? 'text.secondary' : 'error.main'}
                        sx={{ ml: 0.5 }}
                      >
                        {item.trend}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
        
        {/* Transferencias recientes */}
        <Grid container spacing={3} sx={{ mt: 4 }}>
          <Grid item xs={12} md={8}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardHeader title="Transferencias Recientes" />
              <Divider />
              <CardContent>
                <List>
                  {recentTransfers.map((transfer) => (
                    <React.Fragment key={transfer.id}>
                      <ListItem alignItems="flex-start">
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: transfer.status === 'Completado' ? 'success.light' : 'warning.light' }}>
                            <SwapHoriz />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={transfer.client}
                          secondary={
                            <React.Fragment>
                              <Typography
                                component="span"
                                variant="body2"
                                color="text.primary"
                              >
                                {transfer.amount}
                              </Typography>
                              {` — ${transfer.date} — `}
                              <Typography
                                component="span"
                                variant="body2"
                                color={transfer.status === 'Completado' ? 'success.main' : 'warning.main'}
                              >
                                {transfer.status}
                              </Typography>
                            </React.Fragment>
                          }
                        />
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card elevation={2} sx={{ borderRadius: 2 }}>
              <CardHeader title="Actividad Reciente" />
              <Divider />
              <CardContent>
                <Typography variant="body2" color="text.secondary">
                  Aquí se mostrará la actividad reciente del sistema.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Dashboard;